import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenants, useSyncState } from '../hooks/api';
import { Loading, ErrorBox, EmptyBox } from '../components/Loading';
import { Status, statusKindForJobStatus } from '../components/Status';
import { formatNumber, formatTimestamp } from '../api';

export default function SyncStatePage() {
  const tenants = useTenants();
  const [filter, setFilter] = useState('');

  return (
    <div className="space-y-3">
      <h1 className="text-[14px] font-semibold uppercase tracking-wide text-neutral-700 border-b border-neutral-300 pb-1">
        Sync-State (Cross-Tenant)
      </h1>

      <div className="panel">
        <div className="panel-h">Filter</div>
        <div className="panel-b">
          <input
            type="text"
            className="input w-full"
            placeholder="Tenant-Code..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      {tenants.isLoading && <Loading />}
      {tenants.error && <ErrorBox error={tenants.error} />}

      {tenants.data?.data
        .filter((t) => !filter || t.code.toLowerCase().includes(filter.toLowerCase()))
        .map((t) => (
          <TenantSyncBlock key={t.code} tenantCode={t.code} tenantName={t.name} />
        ))}

      {tenants.data?.data.length === 0 && <EmptyBox>Keine Tenants.</EmptyBox>}
    </div>
  );
}

function TenantSyncBlock({ tenantCode, tenantName }: { tenantCode: string; tenantName: string }) {
  const sync = useSyncState(tenantCode);
  return (
    <div className="panel">
      <div className="panel-h flex items-center justify-between">
        <span>
          {tenantCode} <span className="font-normal text-neutral-500">— {tenantName}</span>
        </span>
        <Link to={`/tenants/${encodeURIComponent(tenantCode)}`} className="text-[11px] underline">
          Tenant-Detail
        </Link>
      </div>
      <div className="panel-b p-0">
        {sync.isLoading && (
          <div className="p-2">
            <Loading />
          </div>
        )}
        {sync.error && (
          <div className="p-2">
            <ErrorBox error={sync.error} />
          </div>
        )}
        {sync.data && sync.data.data.length === 0 && (
          <div className="p-2">
            <EmptyBox>Noch kein Sync-Run.</EmptyBox>
          </div>
        )}
        {sync.data && sync.data.data.length > 0 && (
          <table className="tbl">
            <thead>
              <tr>
                <th>Source</th>
                <th>Entity</th>
                <th>Status</th>
                <th className="num">processed</th>
                <th className="num">created</th>
                <th className="num">updated</th>
                <th>Letzter Full-Sync</th>
                <th>Fehler</th>
              </tr>
            </thead>
            <tbody>
              {sync.data.data.map((r) => (
                <tr key={`${r.source_system}.${r.entity}`}>
                  <td className="mono">{r.source_system}</td>
                  <td className="mono">{r.entity}</td>
                  <td>
                    <Status kind={statusKindForJobStatus(r.status)}>{r.status}</Status>
                  </td>
                  <td className="num">{formatNumber(r.rows_processed)}</td>
                  <td className="num">{formatNumber(r.rows_created)}</td>
                  <td className="num">{formatNumber(r.rows_updated)}</td>
                  <td className="mono">{formatTimestamp(r.last_full_sync_at)}</td>
                  <td className="text-[11px] text-st-err truncate max-w-[300px]">
                    {r.last_error ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
